import React from 'react';
import { usePermissionsContext } from '../../providers/PermissionsProvider';
import { useAuth } from '../../hooks';
import { SCREENS, ACTIONS } from '../../constants/permissions';

/**
 * PermissionGuard - Renders children only if user has required permission
 *
 * @param {Object} props
 * @param {string} props.screen - Screen name (SCREENS constant)
 * @param {string} props.action - Action type (ACTIONS constant)
 * @param {number} props.companyId - Optional company ID
 * @param {React.ReactNode} props.children - Component to render if permission granted
 * @param {React.ReactNode} props.fallback - Component to render if permission denied
 * @param {boolean} props.showFallback - Whether to show fallback or nothing when denied
 */
const PermissionGuard = ({
  screen,
  action,
  companyId = null,
  children,
  fallback = null,
  showFallback = false,
}) => {
  const { hasPermission } = usePermissionsContext();

  if (!hasPermission(screen, action, companyId)) {
    return showFallback ? fallback : null;
  }

  return children;
};

export const ViewGuard = ({
  screen,
  companyId,
  children,
  fallback,
  showFallback,
}) => {
  const { user } = useAuth();

  if (
    screen === SCREENS.LEAVE_REQUESTS &&
    user?.isLoanRequestManager === true
  ) {
    return children;
  }

  return (
    <PermissionGuard
      screen={screen}
      action={ACTIONS.VIEW}
      companyId={companyId}
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </PermissionGuard>
  );
};

/**
 * RoleGuard - Renders children only if user has required role
 *
 * @param {Object} props
 * @param {string|Array} props.roles - Required role(s)
 * @param {React.ReactNode} props.children - Component to render if role matches
 * @param {React.ReactNode} props.fallback - Component to render if role doesn't match
 * @param {boolean} props.showFallback - Whether to show fallback or nothing when denied
 */
export const RoleGuard = ({
  roles,
  children,
  fallback = null,
  showFallback = false,
}) => {
  const { user } = usePermissionsContext();

  if (!user) {
    return showFallback ? fallback : null;
  }

  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  const hasRole = allowedRoles.includes(user.role);

  if (!hasRole) {
    return showFallback ? fallback : null;
  }

  return children;
};

export default PermissionGuard;
